import { Component, OnInit } from "@angular/core";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { MatDialog } from "@angular/material";
import * as moment from "moment";
import { NotificationsService } from "angular2-notifications";
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { UserService } from "../services/user.service";
import { ProperJsonDialogComponent } from "../properJsonDialog/properjsondialog.component";
import { AdjustGatewayWalletDialog } from "./adjust-gateway-wallet/adjust-gateway-wallet.component";
import { wholeAmountFormatter } from "../custompipes/amount-formatter";
import { TableUtil } from "../admintrademaster/tableUtil";

// Admin entries (deposits, adjustments) carry the admin ref; payout entries only the source
const entryByName = (entry) => (entry ? (entry.AdminId && entry.AdminId.name) || entry.EntryBy || "" : "");

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50,
  },
};

const dateFormatter = (params) => (params.value ? moment(params.value).format("MMM DD, YYYY hh:mm:ss A") : "");

@Component({
  selector: "app-gateway-wallet",
  templateUrl: "./gateway-wallet.component.html",
  styleUrls: ["./gateway-wallet.component.css"],
})
export class GatewayWalletComponent implements OnInit {
  _csrf: string;

  private walletGridApi: GridApi;
  private ledgerGridApi: GridApi;
  walletGridOptions: GridOptions;
  ledgerGridOptions: GridOptions;
  walletColumnDefs: ColDef[] = [];
  ledgerColumnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
  };

  wallets: any[] = [];
  totalBalance: number = 0;

  ledger: any[] = [];
  ledgerTotal: number = 0;
  filter: any = this.getDefaultFilter();
  prev_disabled = true;
  next_disabled = false;
  entryTypes: string[] = ["DEPOSIT", "PAYOUT_DEBIT", "PAYOUT_REVERSAL", "ADJUSTMENT"];
  summary: any = {};

  myFilter = (d: Date | null): boolean => true;

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private spinnerService: Ng4LoadingSpinnerService,
    public dialog: MatDialog
  ) {
    this.initializeGrids();
  }

  ngOnInit() {
    this.csrf();
  }

  getDefaultFilter() {
    return {
      page: 1,
      limit: 100,
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      walletKey: "",
      entryType: "ALL",
      payoutOrderId: "",
    };
  }

  initializeGrids() {
    this.walletColumnDefs = [
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => {
          const element = params.data;
          const container = document.createElement("div");
          container.style.display = "flex";
          container.style.alignItems = "center";

          const addBtn = (className: string, title: string, icon: string, onClick: () => void) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = `btn btn-sm ${className}`;
            btn.title = title;
            btn.style.marginRight = "5px";
            btn.innerHTML = `<i class="fa ${icon}"></i>`;
            btn.onclick = (e) => {
              e.stopPropagation();
              onClick();
            };
            container.appendChild(btn);
          };
          addBtn("btn-warning", "Adjust Balance", "fa-balance-scale", () => this.openAdjustDialog(element));
          addBtn("btn-primary", "View Ledger", "fa-list", () => this.viewLedger(element));

          return container;
        },
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 110,
      },
      { headerName: "Gateway", field: "GatewayCode", flex: 1 },
      { headerName: "MID", field: "MId", flex: 1 },
      { headerName: "Name", field: "Name", flex: 1.5 },
      {
        headerName: "Balance",
        field: "Balance",
        flex: 1,
        valueFormatter: wholeAmountFormatter,
        cellStyle: (params) => (params.value <= 0 ? { background: "#ffe6e6" } : null),
      },
      { headerName: "Total Credit", field: "TotalCredit", flex: 1, valueFormatter: wholeAmountFormatter },
      { headerName: "Total Debit", field: "TotalDebit", flex: 1, valueFormatter: wholeAmountFormatter },
      { headerName: "Updated", field: "UpdatedAt", valueFormatter: dateFormatter, flex: 1.5 },
    ];

    this.ledgerColumnDefs = [
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => {
          const element = params.data;
          const viewBtn = document.createElement("button");
          viewBtn.type = "button";
          viewBtn.className = "btn btn-sm btn-info";
          viewBtn.title = "View";
          viewBtn.innerHTML = '<i class="fa fa-eye"></i>';
          viewBtn.onclick = (e) => {
            e.stopPropagation();
            this.displayProperJson(element, "Gateway Ledger");
          };
          return viewBtn;
        },
        sortable: false,
        filter: false,
        flex: 0.7,
        minWidth: 80,
      },
      { headerName: "Date", field: "createdAt", valueFormatter: dateFormatter, flex: 1.5 },
      { headerName: "Wallet", field: "WalletKey", flex: 1.2 },
      { headerName: "Entry Type", field: "EntryType", flex: 1.2 },
      {
        headerName: "Credit/Debit",
        field: "TxnType",
        flex: 1,
        cellStyle: (params) => {
          if (params.value === "CREDIT") return { background: "#e6ffe6" };
          if (params.value === "DEBIT") return { background: "#ffe6e6" };
          return null;
        },
      },
      { headerName: "Amount", field: "Amount", flex: 1, valueFormatter: wholeAmountFormatter },
      { headerName: "Opening", field: "OpeningBalance", flex: 1, valueFormatter: wholeAmountFormatter },
      { headerName: "Closing", field: "ClosingBalance", flex: 1, valueFormatter: wholeAmountFormatter },
      {
        headerName: "Order Id / Attempt",
        field: "PayoutOrderId",
        valueGetter: (params) =>
          params.data && params.data.PayoutOrderId ? `${params.data.PayoutOrderId}${params.data.AttemptNo ? " / " + params.data.AttemptNo : ""}` : "",
        flex: 1.5,
      },
      { headerName: "Deposit Id", field: "DepositId", flex: 1.5 },
      {
        headerName: "Deposit Date",
        field: "DepositDate",
        valueFormatter: (params) => (params.value ? moment(params.value).format("MMM DD, YYYY") : ""),
        flex: 1,
      },
      { headerName: "Reference", field: "Reference", flex: 1.2 },
      { headerName: "Note", field: "Note", flex: 1.5 },
      { headerName: "Entry By", field: "EntryBy", valueGetter: (params) => entryByName(params.data), flex: 1 },
    ];

    const commonOptions: GridOptions = {
      defaultColDef: this.defaultColDef,
      pagination: false,
      suppressHorizontalScroll: false,
      enableRangeSelection: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40,
    };

    this.walletGridOptions = {
      ...commonOptions,
      columnDefs: this.walletColumnDefs,
      // Inactive gateways are shown dimmed
      getRowStyle: (params) => (params.data && !params.data.IsGatewayActive ? { opacity: "0.5" } : null),
    };
    this.ledgerGridOptions = { ...commonOptions, columnDefs: this.ledgerColumnDefs };
  }

  onWalletGridReady(params) {
    this.walletGridApi = params.api;
    this.walletGridApi.sizeColumnsToFit();
  }

  onLedgerGridReady(params) {
    this.ledgerGridApi = params.api;
    this.ledgerGridApi.sizeColumnsToFit();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.getWallets();
        this.getLedger();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, defaults.notif);
      }
    );
  }

  getWallets() {
    this.userService.getGatewayWallets().subscribe(
      (response: any) => {
        this.wallets = (response && response.data) || [];
        this.totalBalance = this.wallets.reduce((sum, w) => sum + (w.Balance || 0), 0);
        if (this.walletGridApi) {
          this.walletGridApi.setRowData(this.wallets);
        }
      },
      () => {
        this.notifSer.error("Error", "Failed to load gateway wallets", defaults.notif);
      }
    );
  }

  search() {
    this.filter.page = 1;
    this.getLedger();
  }

  goNext() {
    this.filter.page++;
    this.getLedger();
  }

  goPrev() {
    if (this.filter.page > 1) {
      this.filter.page--;
      this.getLedger();
    }
  }

  fromDateFilter(event: any) {
    if (event.value) {
      this.filter["fromDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  toDateFilter(event: any) {
    if (event.value) {
      this.filter["toDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  getLedger() {
    this.spinnerService.show();
    this.filter._csrf = this._csrf;
    this.userService.getGatewayLedger(this.filter).subscribe(
      (response: any) => {
        this.ledger = (response && response.data) || [];
        this.ledgerTotal = (response && response.total) || 0;
        if (this.ledgerGridApi) {
          this.ledgerGridApi.setRowData(this.ledger);
        }
        this.spinnerService.hide();
        this.prev_disabled = this.filter.page <= 1;
        this.next_disabled = this.filter.page * this.filter.limit >= this.ledgerTotal;
      },
      () => {
        this.notifSer.error("Error", "Failed to load gateway ledger", defaults.notif);
        this.spinnerService.hide();
      }
    );

    this.userService.getGatewayLedgerSummary(this.filter).subscribe((response: any) => {
      this.summary = (response && response.data) || {};
    });
  }

  // Exports every entry matching the current filters (not just the current page) to Excel
  exportLedger() {
    this.spinnerService.show();
    const exportFilter = { ...this.filter, _csrf: this._csrf, page: 1, limit: 5000 };
    this.userService.getGatewayLedger(exportFilter).subscribe(
      (response: any) => {
        const list = (response && response.data) || [];
        if (list.length > 0) {
          const exportData = list.map((x: any) => ({
            Date: moment(x.createdAt).format("YYYY-MM-DD HH:mm:ss"),
            Wallet: x.WalletKey,
            Gateway: x.GatewayCode,
            MID: x.MId,
            EntryType: x.EntryType,
            TxnType: x.TxnType,
            Amount: x.Amount,
            OpeningBalance: x.OpeningBalance,
            ClosingBalance: x.ClosingBalance,
            PayoutOrderId: x.PayoutOrderId || "",
            AttemptNo: x.AttemptNo || "",
            DepositId: x.DepositId || "",
            DepositDate: x.DepositDate ? moment(x.DepositDate).format("YYYY-MM-DD") : "",
            Reference: x.Reference || "",
            Note: x.Note || "",
            EntryBy: entryByName(x),
            RefId: x.RefId,
          }));
          TableUtil.exportArrayToExcel(exportData, "GatewayLedger");
          if ((response.total || 0) > list.length) {
            this.notifSer.info("Info", `Exported the latest ${list.length} of ${response.total} entries; narrow the dates to export the rest`, defaults.notif);
          }
        } else {
          this.notifSer.info("Info", "No data available to export", defaults.notif);
        }
        this.spinnerService.hide();
      },
      () => {
        this.notifSer.error("Error", "Failed to export data. Please try again.", defaults.notif);
        this.spinnerService.hide();
      }
    );
  }

  resetFilters() {
    this.filter = this.getDefaultFilter();
    this.getLedger();
  }

  viewLedger(wallet: any) {
    this.filter.walletKey = wallet.WalletKey;
    this.search();
  }

  openAdjustDialog(wallet: any) {
    const dialogRef = this.dialog.open(AdjustGatewayWalletDialog, {
      width: "600px",
      maxWidth: "90vw",
      disableClose: true,
      data: { wallet: wallet, _csrf: this._csrf },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      const notif = { ...defaults.notif, timeOut: 5000, maxLength: 150 };
      if (result.failed) {
        this.notifSer.error("Failed", result.message, notif);
      } else if (result.pending) {
        this.notifSer.info("Submitted", result.message, notif);
      } else {
        this.notifSer.success("Success", result.message, notif);
      }
      this.getWallets();
      this.getLedger();
    });
  }

  displayProperJson(jsonData, eventType) {
    const obj = {
      jsonData: jsonData,
      eventType: eventType || "Gateway Ledger",
    };
    this.dialog
      .open(ProperJsonDialogComponent, {
        width: "auto",
        height: "auto",
        data: obj,
      })
      .afterClosed()
      .subscribe(() => { });
  }
}



// WEBPACK FOOTER //
// ./src/app/gateway-wallet/gateway-wallet.component.ts